import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import httpInstances from '@/api/axiosInstance';
import { API_SYNC_DELAY_MS } from '@/constants/constants';
import { DASHBOARD_PREFIX } from '@/features/Dashboard/constants/const';
import {
  DASHBOARD_DEFAULT_BASE_NAME,
  TAB_DEFAULT_BASE_NAME
} from '@/features/Dashboard/constants/names';
import { buildDashboardPayload, normalizeTabEntry } from '@/features/Dashboard/lib/transformers';
import {
  selectCurrentTabName,
  selectDashboardName,
  selectEditStateForSave,
  selectInitialDashboardName
} from '@/features/Dashboard/pages/Edit/model/dashboardEdit.selectors';
import { mapDraftToServerPayload } from '@/features/Dashboard/pages/Edit/model/utils/mapping';
import type {
  DashboardTab,
  MarketApps,
  NestedDashboardStructure,
  TabEntry
} from '@/features/Dashboard/types';
import { getUniqueDashboardName } from '@/features/Dashboard/utils/getUniqueDashboardName';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import usePreferences from '@/pages/Profile/Preferences/usePreferences';
import type { Option, UserConfig } from '@/slices/userConfigSlice';
import { saveUserConfig } from '@/slices/userConfigSlice';
import type { AppDispatch } from '@/store';
import { store } from '@/store';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

import { getDashboardApiUrl } from './dashboardUrl';
import { dashboardQueryKeys } from './queries'; // ============================================================================
/**
 * Save dashboard changes to server.
 * Builds payload from Redux draft state.
 *
 * Note: State is read inside mutationFn to avoid stale closure issues.
 * Using selectors at hook level would capture state at render time,
 * not at mutation execution time.
 */

// ============================================================================
// Constants
// ============================================================================

export const SAVE_SNACKBAR_KEY = 'dashboard-save';

// ============================================================================
// Types
// ============================================================================

/**
 * Metadata for bulk dashboard update to support rename/navigate/homepage side effects
 */
export type BulkUpdateMeta = {
  /** New dashboard name (after edit) - used for navigation and homepage update */
  dashboardName?: string;
  /** Old dashboard name (before edit) - if present and different from dashboardName, triggers rename logic */
  oldDashboardName?: string;
  /** Current tab name to preserve in URL after rename navigation */
  tabName?: string;
  /** Success message to show in snackbar */
  successMessage?: string;
};

/**
 * Input type union for dashboard updates - either a full structure or tab content
 */
export type DashboardUpdateInput = MarketApps | NestedDashboardStructure;

// ============================================================================
// Type Guards (pure, testable)
// ============================================================================

/**
 * Type guard: checks if value is a TabEntry (new format with id and content)
 */
const isTabEntry = (value: MarketApps | TabEntry): value is TabEntry => {
  return (
    !Array.isArray(value) && 'id' in value && 'content' in value && Array.isArray(value.content)
  );
};

/**
 * Type guard: checks if input is a full NestedDashboardStructure
 * (has dashboard name as key with non-empty tab object)
 */
export const isFullDashboardStructure = (
  input: DashboardUpdateInput,
  dashboardName: string
): input is NestedDashboardStructure => {
  if (Array.isArray(input)) return false;

  // After array check, input is NestedDashboardStructure
  // Use 'in' operator for TypeScript-friendly key existence check
  if (!(dashboardName in input)) return false;

  const dashboardEntry = input[dashboardName];
  return Object.keys(dashboardEntry).length > 0;
};

// ============================================================================
// Pure Helper Functions (testable without React)
// ============================================================================

/**
 * Delays execution for API synchronization
 */
export const waitForApiSync = (): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, API_SYNC_DELAY_MS);
  });

/**
 * Gets the effective tab name, falling back to default if empty/undefined
 */
export const getEffectiveTabName = (tabName?: string): string => tabName || TAB_DEFAULT_BASE_NAME;

/**
 * Determines if a rename operation is occurring
 */
export const isRenameOperation = (oldName: string | undefined, newName: string): boolean =>
  Boolean(oldName && oldName !== newName);

/**
 * Checks if homepage value matches a dashboard (with or without prefix)
 */
export const homepageMatchesDashboard = (homepageValue: string, dashboardName: string): boolean =>
  homepageValue === DASHBOARD_PREFIX + dashboardName || homepageValue === dashboardName;

/**
 * Builds the dashboard URL path
 */
export const buildDashboardPath = (dashboardName: string, tabName?: string): string => {
  const effectiveTab = getEffectiveTabName(tabName);
  return `/dashboard/${encodeURIComponent(dashboardName)}/${encodeURIComponent(effectiveTab)}`;
};

/**
 * Creates a homepage option from dashboard name
 */
export const createHomepageOption = (dashboardName: string): Option => ({
  label: dashboardName,
  value: DASHBOARD_PREFIX + dashboardName
});

interface BuildMergedPayloadParams {
  data: DashboardUpdateInput;
  dashboardName: string;
  tabName: string;
  oldDashboardName: string | undefined;
  cached: Record<string, DashboardTab> | undefined;
}

/**
 * Builds merged payload for dashboard update.
 * Handles both full structure updates and single tab updates.
 *
 * @returns Merged DashboardTab ready for API
 */
export const buildMergedPayload = ({
  data,
  dashboardName,
  tabName,
  oldDashboardName,
  cached
}: BuildMergedPayloadParams): DashboardTab => {
  // Full structure update - return dashboard tabs directly
  if (isFullDashboardStructure(data, dashboardName)) {
    return data[dashboardName];
  }

  // Single tab update - merge with existing tabs (data is MarketApps at this point)
  const newTabPayload = buildDashboardPayload(data, dashboardName, tabName);
  const sourceDashboardName = oldDashboardName || dashboardName;
  const existingTabs = cached?.[sourceDashboardName] ?? {};
  const effectiveTabName = getEffectiveTabName(tabName);

  // Copy existing tabs preserving order
  const updatedTabs: DashboardTab = {};
  for (const [name, value] of Object.entries(existingTabs)) {
    updatedTabs[name] = normalizeTabEntry(name, value);
  }

  // Update or create the target tab
  const newTabValue = newTabPayload[effectiveTabName];
  const existingTabValue = existingTabs[effectiveTabName] as MarketApps | TabEntry | undefined;
  const normalizedExisting = existingTabValue
    ? normalizeTabEntry(effectiveTabName, existingTabValue)
    : null;

  if (isTabEntry(newTabValue)) {
    updatedTabs[effectiveTabName] = {
      id: normalizedExisting?.id || newTabValue.id,
      content: newTabValue.content
    };
  } else {
    // Fallback: create tab with provided data or empty array
    updatedTabs[effectiveTabName] = {
      id: normalizedExisting?.id || generateUniqueUUID(new Set()),
      content: Array.isArray(data) ? data : []
    };
  }

  return updatedTabs;
};

// ============================================================================
// Redux Helper (side-effectful, but isolated)
// ============================================================================

/**
 * Updates user's homepage setting in Redux store
 */
const dispatchHomepageUpdate = (
  option: Option,
  dispatch: AppDispatch,
  userConfig: UserConfig
): void => {
  dispatch(
    saveUserConfig({
      userConfig: {
        ...userConfig,
        homepage: option
      }
    })
  );
};

// ============================================================================
// API Functions (pure async, testable with mocked axios)
// ============================================================================

/**
 * Sends dashboard update to API (POST or PATCH for rename)
 */
export const saveDashboardToApi = async (
  dashboardName: string,
  payload: DashboardTab,
  oldDashboardName?: string
): Promise<void> => {
  const isRename = isRenameOperation(oldDashboardName, dashboardName);

  if (isRename && oldDashboardName) {
    await httpInstances.axiosInternalInstance.patch(
      getDashboardApiUrl(encodeURIComponent(oldDashboardName)),
      payload,
      { params: { newKey: dashboardName } }
    );
  } else {
    await httpInstances.axiosInternalInstance.post(
      getDashboardApiUrl(encodeURIComponent(dashboardName)),
      payload
    );
  }

  await waitForApiSync();
};

/**
 * Sends bulk dashboard update to API
 */
export const saveDashboardsBulkToApi = async (
  fullStructure: Record<string, DashboardTab>
): Promise<void> => {
  await httpInstances.axiosInternalInstance.post(getDashboardApiUrl(), fullStructure);
  await waitForApiSync();
};

/**
 * Deletes a dashboard by name
 */
export const deleteDashboardFromApi = async (dashboardName: string): Promise<void> => {
  await httpInstances.axiosInternalInstance.delete(
    getDashboardApiUrl(encodeURIComponent(dashboardName))
  );
  await waitForApiSync();
};

// ============================================================================
// React Hooks (composition of pure functions + React dependencies)
// ============================================================================

/**
 * Hook for updating a single dashboard.
 * Supports:
 * - Single tab content updates
 * - Full dashboard structure updates
 * - Dashboard rename (when oldDashboardName differs from dashboardName)
 * - Optimistic cache updates with rollback on error
 */
export const useUpdateDashboard = (
  dashboardName = DASHBOARD_DEFAULT_BASE_NAME,
  successMessage = 'Dashboard updated!',
  tabName = TAB_DEFAULT_BASE_NAME,
  oldDashboardName?: string
) => {
  const { showErrorSnackbar, showSuccessSnackbar, showLoadingSnackbar } =
    useSnackbarNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { changeUserSetting, homepage } = usePreferences();

  const queryKey = dashboardQueryKeys.all;

  return useMutation({
    mutationKey: ['dashboard', dashboardName, oldDashboardName],

    mutationFn: async (data: DashboardUpdateInput): Promise<string> => {
      showLoadingSnackbar('Saving dashboard...', SAVE_SNACKBAR_KEY);

      const cached = queryClient.getQueryData<Record<string, DashboardTab>>(queryKey);
      const existingNames = cached ? Object.keys(cached) : [];

      const isNewDashboard = dashboardName === 'new';
      const effectiveDashboardName = isNewDashboard
        ? getUniqueDashboardName(existingNames)
        : dashboardName;

      // When creating a new dashboard, the data structure has 'new' as key.
      // We need to remap it to the generated name.
      let processedData = data;
      if (isNewDashboard && !Array.isArray(data) && 'new' in data) {
        const { new: newDashboardTabs, ...rest } = data;
        processedData = { ...rest, [effectiveDashboardName]: newDashboardTabs };
      }

      const payload = buildMergedPayload({
        data: processedData,
        dashboardName: effectiveDashboardName,
        tabName,
        oldDashboardName,
        cached
      });

      await saveDashboardToApi(effectiveDashboardName, payload, oldDashboardName);

      return effectiveDashboardName;
    },

    onMutate: async (_data: DashboardUpdateInput) => {
      await queryClient.cancelQueries({ queryKey: dashboardQueryKeys.all });

      const previousCache = queryClient.getQueryData<Record<string, DashboardTab>>(queryKey);

      return { previousCache };
    },

    onError: (_error, _data, context) => {
      if (context?.previousCache) {
        queryClient.setQueryData(queryKey, context.previousCache);
      }
      showErrorSnackbar("Oops! We couldn't save your dashboard. Please try again.");
    },

    onSuccess: effectiveDashboardName => {
      if (successMessage) {
        showSuccessSnackbar(successMessage, SAVE_SNACKBAR_KEY);
      }

      void queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all });

      // Navigate when names differ (includes undefined oldDashboardName - matches original behavior)
      if (oldDashboardName !== effectiveDashboardName) {
        navigate(buildDashboardPath(effectiveDashboardName, tabName));

        // Update homepage if it pointed to the old dashboard name
        if (oldDashboardName && homepageMatchesDashboard(homepage.value, oldDashboardName)) {
          changeUserSetting('homepage', createHomepageOption(effectiveDashboardName), false);
          return;
        }
      }

      // Handle default dashboard homepage update
      if (
        effectiveDashboardName === DASHBOARD_DEFAULT_BASE_NAME &&
        homepage.value === 'my-dashboard'
      ) {
        changeUserSetting('homepage', createHomepageOption(effectiveDashboardName), false);
      }
    }
  });
};

/**
 * Hook for deleting a dashboard.
 * Handles:
 * - Cache invalidation and optimistic removal
 * - Navigation to next available dashboard (or /dashboard/new)
 * - Homepage update if deleted dashboard was the homepage
 */
export const useDeleteDashboard = (dashboardName: string) => {
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userConfig = useAppSelector(state => state.userConfig.userConfig);
  const homepage = userConfig.homepage;

  const queryKey = dashboardQueryKeys.all;

  return useMutation({
    mutationKey: ['dashboard', 'delete', dashboardName],

    mutationFn: () => deleteDashboardFromApi(dashboardName),

    onError: () => {
      showErrorSnackbar("Oops! We couldn't delete your dashboard. Please try again.");
    },

    onSuccess: () => {
      showSuccessSnackbar('Dashboard deleted!');

      // Optimistically remove from cache
      const cached = queryClient.getQueryData<Record<string, DashboardTab>>(queryKey);
      let cacheAfterDelete = cached;

      if (cached && dashboardName in cached) {
        const updatedCache = { ...cached };
        delete updatedCache[dashboardName];
        queryClient.setQueryData(queryKey, updatedCache);
        cacheAfterDelete = updatedCache;
      }

      // Navigate to first remaining dashboard or new dashboard page
      const remainingDashboards = cacheAfterDelete
        ? Object.keys(cacheAfterDelete).filter(name => name !== dashboardName)
        : [];

      const wasHomepage = homepage.value === DASHBOARD_PREFIX + dashboardName;

      if (remainingDashboards.length > 0) {
        const firstDashboard = remainingDashboards[0];
        const firstDashboardTabs = cacheAfterDelete?.[firstDashboard];

        if (firstDashboardTabs) {
          const firstTabName = Object.keys(firstDashboardTabs)[0];
          navigate(buildDashboardPath(firstDashboard, firstTabName));
        }

        if (wasHomepage) {
          dispatchHomepageUpdate(createHomepageOption(firstDashboard), dispatch, userConfig);
        }
      } else {
        navigate('/dashboard/new');

        if (wasHomepage) {
          dispatchHomepageUpdate(
            { label: DASHBOARD_DEFAULT_BASE_NAME, value: 'my-dashboard' },
            dispatch,
            userConfig
          );
        }
      }

      void queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all });
    }
  });
};

/**
 * Result type for useSaveDashboard mutation
 */
type SaveDashboardResult = {
  dashboardName: string;
  initialDashboardName: string;
  tabName: string;
  fullPayload: any;
};

type SaveCtx = {
  previous: Record<string, DashboardTab> | undefined;
  dashboardName: string;
  initialDashboardName: string;
  tabName: string;
  fullPayload: Record<string, DashboardTab>;
};

export const useSaveDashboard = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showErrorSnackbar, showSuccessSnackbar, showLoadingSnackbar } =
    useSnackbarNotifications();
  const { changeUserSetting, homepage } = usePreferences();

  return useMutation({
    mutationFn: async (): Promise<SaveDashboardResult> => {
      await queryClient.cancelQueries({ queryKey: dashboardQueryKeys.all });
      showLoadingSnackbar('Saving dashboard...', SAVE_SNACKBAR_KEY);

      // Get FRESH state at mutation execution time (avoids stale closure)
      const state = store.getState();
      const editState = selectEditStateForSave(state);
      const dashboardName = selectDashboardName(state);
      const initialDashboardName = selectInitialDashboardName(state);
      const tabName = selectCurrentTabName(state);

      // Build server payload from draft
      const payload = mapDraftToServerPayload(editState);

      // Get cached dashboard data
      const cached =
        queryClient.getQueryData<Record<string, DashboardTab>>(dashboardQueryKeys.all) || {};

      // Deep merge: preserves existing tabs while adding/updating from payload
      const fullPayload = { ...cached, ...payload };

      // Handle rename case - remove old dashboard name
      if (dashboardName !== initialDashboardName && initialDashboardName in fullPayload) {
        delete fullPayload[initialDashboardName];
      }

      await httpInstances.axiosInternalInstance.post(getDashboardApiUrl(), fullPayload);
      await waitForApiSync();

      return { dashboardName, initialDashboardName, tabName, fullPayload };
    },

    onSuccess: ({ dashboardName, initialDashboardName, tabName, fullPayload }) => {
      showSuccessSnackbar('Dashboard saved!', SAVE_SNACKBAR_KEY);
      queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all });
      // Handle rename side effects
      if (isRenameOperation(initialDashboardName, dashboardName)) {
        navigate(buildDashboardPath(dashboardName, tabName));

        if (homepageMatchesDashboard(homepage.value, initialDashboardName)) {
          changeUserSetting('homepage', createHomepageOption(dashboardName), false);
        }
      }
    },

    onError: () => {
      showErrorSnackbar("Oops! We couldn't save your dashboard. Please try again.");
    }
  });
};
